import React from 'react';
import Module from 'Components/Core/Module';
import IbBrokerageOrderForm from 'Components/Banking/IbBrokerageOrderForm';
import IbBrokerageStopForm from 'Components/Banking/IbBrokerageStopForm';
import IbBrokerageCashDialog from 'Components/Banking/IbBrokerageCashDialog';
import { registerBrokerageDataListener } from 'Components/Banking/brokerageDataBus';
import './Brokerage.css';

// Nutzervorgabe, Chat vom 2026-08-08: "Aktien gibt es nur Ganze oder gar
// nicht, Nachkommastellen sind unsinnig" - Stückzahl IMMER ganzzahlig
// runden/formatieren, egal was IB/die DB an Nachkommastellen liefert
// (decimal-Spalte, siehe ibBrokeragePositions-Migration).
function formatQuantity(value) {
	if (value == null || value === '') return '–';
	return Math.round(parseFloat(value)).toLocaleString('de-DE');
}

// G/V ebenfalls ohne Nachkommastellen (Nutzervorgabe, Chat vom 2026-08-08).
function formatWholeNumber(value) {
	if (value == null || value === '') return '';
	return Math.round(parseFloat(value)).toLocaleString('de-DE');
}

// Depot-Gesamtsumme in EUR (Nutzerwunsch, Chat vom 2026-08-08) - 2
// Nachkommastellen wie beim Comdirect-Pendant (BrokerageOverview.js
// formatValue()), bewusst NICHT die Ganzzahl-Regel fuer Stueckzahlen/G-V
// (die gilt nur dort, siehe Kommentare oben).
// Kurs-/Wert-Spalten (Einstand, Kurs, Wert): max. 3 Nachkommastellen
// (Nutzerwunsch, Chat vom 2026-08-10) - IB/PHP liefern hier Roh-Floats mit
// Gleitkomma-Artefakten (z.B. 3926.3849999999998); toLocaleString rundet auf
// 3 Stellen und schneidet ueberfluessige Nullen ab (deutsches Format wie bei
// Stück/G-V).
function formatDecimal(value) {
	if (value == null || value === '') return '';
	const parsed = parseFloat(value);
	return Number.isNaN(parsed) ? '' : parsed.toLocaleString('de-DE', { maximumFractionDigits: 3 });
}

function formatValue(value) {
	if (value === undefined || value === null || value === '') return '–';
	const parsed = parseFloat(value);
	return Number.isNaN(parsed) ? '–' : parsed.toFixed(2);
}

// IB-Positionsuebersicht (Chat vom 2026-08-07, Phase 2 der IBKR-Integration)
// - Pendant zu BrokerageOverview.js (Comdirect), aber deutlich einfacher:
// keine Depot-/Verbindungsauswahl (IB hat nur EIN Gateway/Account pro
// Instanz), keine Login-Verwaltung (die IB-Gateway-Session laeuft dauerhaft
// im Node-Worker, siehe IbBrokerageClient.mjs). Reine Lese-Ansicht der
// lokalen Spiegelung (ibBrokeragePositions), aktualisiert per Reload-Icon.
export default function IbBrokerageOverview(props) {
	const x = props.x;
	const [positions, setPositions] = React.useState(null);
	const [totalEur, setTotalEur] = React.useState(null);
	const [status, setStatus] = React.useState(null);
	const [error, setError] = React.useState('');
	const [orderTarget, setOrderTarget] = React.useState(null);
	// Stop-Loss-Dialog (Chat vom 2026-08-11) - Ziel-Position, fuer die ein Stop
	// gesetzt/geaendert/entfernt wird.
	const [stopTarget, setStopTarget] = React.useState(null);
	// Cashbestand je Waehrung (Chat vom 2026-08-08, Nutzerwunsch) - eigener
	// Ladevorgang/Endpunkt (IbBrokerageCash), unabhaengig von loadPositions().
	const [cashBalances, setCashBalances] = React.useState(null);
	const [cashTotalEur, setCashTotalEur] = React.useState(null);
	const [showCashDialog, setShowCashDialog] = React.useState(false);
	// Chat vom 2026-08-08 (Bugreport: "Klick auf Reload-Icon bleibt
	// wirkungslos") - der Klick loeste tatsaechlich schon einen Neu-Ladevorgang
	// aus, aber ohne jede sichtbare Rueckmeldung (anders als beim Comdirect-
	// Pendant, siehe BrokerageOverview.js "syncing"/"brokerage-icon-spin")
	// wirkte das wie ein wirkungsloser Klick, besonders wenn sich die Werte
	// zufaellig nicht geaendert hatten.
	const [reloading, setReloading] = React.useState(false);

	function loadPositions(onDone) {
		x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=IbBrokeragePositions', {}, (res) => {
			if (res.data.returncode !== 1) {
				setError('Positionen konnten nicht geladen werden.');
				if (onDone) onDone();
				return;
			}
			setPositions(res.data.content.positions || []);
			setTotalEur(res.data.content.totalEur);
			if (onDone) onDone();
		});
	}

	function loadCash() {
		x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=IbBrokerageCash', {}, (res) => {
			if (res.data.returncode !== 1) {
				return;
			}
			setCashBalances(res.data.content.balances || []);
			setCashTotalEur(res.data.content.totalEur);
		});
	}

	function loadStatus() {
		x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=IbBrokerageStatus', {}, (res) => {
			if (res.data.returncode === 1) {
				setStatus(res.data.content);
			}
		});
	}

	function reload() {
		setReloading(true);
		loadStatus();
		loadCash();
		loadPositions(() => setReloading(false));
	}

	React.useEffect(() => {
		loadPositions();
		loadStatus();
		loadCash();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		return registerBrokerageDataListener(x, () => { loadPositions(); loadStatus(); loadCash(); });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Module id={props.id} x={x} classes="module-ib-brokerage-overview" title={props.title} display="d-block">
			{/* Chat vom 2026-08-08 (Bugreport: zu viel Platz, Zeilen nicht
			    ausgerichtet) - Gateway-/Trading-Zeile jetzt als enger 2-Zeilen-
			    Block mit gemeinsamer Label-Spaltenbreite (Doppelpunkte/Werte
			    stehen dadurch untereinander), Icon und Gesamtsumme oben
			    ausgerichtet (align-items:flex-start) statt vertikal zentriert. */}
			<div className="brokerage-toolbar ib-brokerage-toolbar">
				<i className={'bi bi-arrow-repeat brokerage-action-icon brokerage-toolbar-reload' + (reloading ? ' brokerage-icon-spin' : '')} title="Neu laden" onClick={reload}></i>
				{/* Chat vom 2026-08-08 (Bugreport per Screenshot: Doppelpunkt/Wert
				    NICHT bündig - "IB-Gateway:" ist selbst schon breiter als das
				    vorherige feste min-width:74px, dazu fehlte der Abstand zum
				    Wert). CSS-Grid statt geschaetzter Pixelbreite: Spalte 1 setzt
				    sich automatisch auf die Breite des laengeren Labels
				    ("IB-Gateway:"), Spalte 2 beginnt garantiert an derselben
				    Stelle in beiden Zeilen - unabhaengig von Schriftart/-groesse. */}
				{status &&
					<div className="ib-brokerage-status">
						<span className="ib-brokerage-status-label">IB-Gateway:</span>
						<span className={status.gatewayConnected ? 'brokerage-side-buy' : 'brokerage-side-sell'}>{status.gatewayConnected ? 'verbunden' : 'nicht verbunden'}</span>
						<span className="ib-brokerage-status-label">Trading:</span>
						<span className={status.tradingEnabled ? 'brokerage-side-buy' : 'brokerage-side-sell'}>{status.tradingEnabled ? 'aktiv' : 'inaktiv'}</span>
					</div>
				}
				{/* Chat vom 2026-08-08 (Nutzerwunsch: "WP"/"Cash"-Label davor,
				    Icon rechts weg - Cash-Zeile stattdessen selbst klickbar,
				    damit EUR wieder rechtsbuendig mit der WP-Zeile steht) -
				    Depot-Summe ("WP") und Cash-Summe zusammen in einem
				    rechtsbuendigen, gestapelten Block (margin-left:auto NUR auf
				    dem gemeinsamen Wrapper, nicht auf beiden einzeln - sonst
				    landen sie als zwei separate Flex-Items nebeneinander statt
				    untereinander). */}
				{((positions !== null && positions.length > 0) || cashTotalEur !== null) &&
					<div className="ib-brokerage-totals">
						{positions !== null && positions.length > 0 &&
							<div>WP: <strong>{formatValue(totalEur)} EUR</strong></div>
						}
						{cashTotalEur !== null &&
							<div className="ib-brokerage-cash-summary" title="Cashbestand je Währung anzeigen" onClick={() => setShowCashDialog(true)}>
								Cash: <strong>{formatValue(cashTotalEur)} EUR</strong>
							</div>
						}
					</div>
				}
			</div>
			{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
			{positions === null && <p>Lade…</p>}
			{positions !== null && positions.length === 0 && <p className="brokerage-hint">Keine offenen Positionen.</p>}
			{positions !== null && positions.length > 0 &&
				<div className="brokerage-table-scroll">
					{/* Chat vom 2026-08-08 (Bugreport: "CSS stimmt nicht") -
					    ".brokerage-order-row" hat ein grid-template-columns fuer die
					    9-spaltige Orderbuch-Tabelle (Symbol/Richtung/Typ/Stück/
					    Gefüllt/Limit/Status/Aktionen), diese Tabelle hat aber nur 7
					    Spalten - dadurch verschoben sich Kurs/Wert/G-V sichtbar. Jetzt
					    ".brokerage-position" (comdirects eigene 7-Spalten-Positions-
					    Zeile, siehe BrokerageOverview.js), passt exakt. */}
					<div className="brokerage-position brokerage-position-header">
						<span>Symbol</span>
						<span>Stück</span>
						{/* Nutzervorgabe, Chat vom 2026-08-08: "Einstand" als Title genügt -
					    "Ø " brach auf Mobil in eine eigene Zeile um. */}
					<span title="Ø Einstand">Einstand</span>
						<span>Kurs</span>
						<span>Wert</span>
						<span title="Gewinn/Verlust in Euro (zum aktuellen Tageskurs umgerechnet)">G/V €</span>
						<span>Stop</span>
						<span></span>
					</div>
					{positions.map((p) => (
						<div className="brokerage-position" key={p.account + '-' + p.conid}>
							<span className="brokerage-order-instrument">{p.symbol}</span>
							<span>{formatQuantity(p.quantity)}</span>
							<span>{formatDecimal(p.avgcost)}</span>
							<span>{formatDecimal(p.marketprice)}</span>
							<span>{formatDecimal(p.marketvalue)}</span>
							{/* G/V in EUR (Chat vom 2026-08-12): unrealizedpnleur kommt aus
							    IbBrokeragePositions.inc (nativer PnL x aktueller FX-Faktor).
							    Fallback auf den nativen Wert, falls der Tages-FX-Kurs fuer
							    die Waehrung noch nicht vorliegt. */}
							<span className={parseFloat(p.unrealizedpnleur ?? p.unrealizedpnl) >= 0 ? 'brokerage-side-buy' : 'brokerage-side-sell'}>{formatWholeNumber(p.unrealizedpnleur ?? p.unrealizedpnl)}</span>
							{/* Stop-Loss (Chat vom 2026-08-11): gesetzter Stopkurs mit
							    Schild-Icon (klickbar zum Aendern/Entfernen) bzw. leeres
							    Schild-Icon zum Setzen. Richtung/Menge aus der Position. */}
							<span
								className={'ib-position-stop' + (p.stopprice != null ? ' ib-position-stop-active' : '')}
								title={p.stopprice != null ? 'Stop-Loss ändern/entfernen' : 'Stop-Loss setzen'}
								onClick={() => setStopTarget({
									conId: parseInt(p.conid, 10),
									symbol: p.symbol,
									secType: p.sectype,
									exchange: 'SMART',
									currency: p.currency,
									action: parseFloat(p.quantity) >= 0 ? 'SELL' : 'BUY',
									quantity: Math.abs(Math.round(parseFloat(p.quantity))),
									currentPrice: p.marketprice,
									stopPrice: p.stopprice ?? '',
									orderId: p.stoporderid != null ? parseInt(p.stoporderid, 10) : null,
									label: p.symbol,
								})}
							>
								<i className={'bi ' + (p.stopprice != null ? 'bi-shield-fill-check' : 'bi-shield')}></i>
								{p.stopprice != null && <span className="ib-position-stop-price">{formatDecimal(p.stopprice)}</span>}
							</span>
							<span className="brokerage-order-actions">
								<i
									className="bi bi-dash-circle brokerage-action-icon brokerage-action-sell"
									title="Verkaufen"
									onClick={() => setOrderTarget({ conId: parseInt(p.conid, 10), symbol: p.symbol, secType: p.sectype, exchange: 'SMART', currency: p.currency, action: 'SELL' })}
								></i>
							</span>
						</div>
					))}
				</div>
			}
			{orderTarget &&
				<div className="brokerage-overlay">
					<IbBrokerageOrderForm x={x} target={orderTarget} onCancel={() => setOrderTarget(null)} onDone={() => { setOrderTarget(null); loadPositions(); }} />
				</div>
			}
			{stopTarget &&
				<div className="brokerage-overlay">
					<IbBrokerageStopForm x={x} target={stopTarget} onCancel={() => setStopTarget(null)} onDone={() => { setStopTarget(null); loadPositions(); }} />
				</div>
			}
			{showCashDialog &&
				<div className="brokerage-overlay">
					<IbBrokerageCashDialog balances={cashBalances} totalEur={cashTotalEur} onClose={() => setShowCashDialog(false)} />
				</div>
			}
		</Module>
	);
}
